import { useCallback, useEffect, useMemo, useState, ReactNode } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { AnimatePresence, motion } from 'motion/react';
import { useQuery } from 'react-query';
import { VoicemailConfigAPI } from '@frontend/api-voicemail-boxes';
import { useTranslation } from '@frontend/i18n';
import { SettingsTenantLocation } from '@frontend/phone-config';
import { theme } from '@frontend/theme';
import {
  ChecklistMenuField,
  DropdownField,
  Heading,
  ListField,
  Button,
  Text,
  TextField,
  useTooltip,
  CheckboxField,
} from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { trackingId } from '../../tracking';
import { SettingsCard, SettingsSection } from '../common/settings-card';
import { GreetingsCard } from './greetings';
import { SchemaForm } from './schema';
import { VoicemailBoxExtension, CombinedData, VoicemailGreeting } from './types';
import { useNotificationValidation } from './use-notification-validation';
import { getExtensionConfig } from './utils';

dayjs.extend(localizedFormat);

export const VoicemailBoxContent = ({
  data,
  tenantLocation,
  form,
  hasPhoneFullAccess,
  onSave,
  onGreetingModifiedChange,
  onGreetingsChange: onGreetingsChangeFromParent,
  tableLayout,
}: {
  data: CombinedData;
  tenantLocation: SettingsTenantLocation;
  form: SchemaForm;
  hasPhoneFullAccess: boolean;
  onSave?: () => void | Promise<void>;
  onGreetingModifiedChange?: (isModified: boolean) => void;
  onGreetingsChange?: (greetings: VoicemailGreeting[]) => void;
  tableLayout?: 'combined' | 'separated';
}) => {
  const [box, _setBox] = useState(() => data);
  const { t } = useTranslation('phone');
  const [isGreetingModified, setIsGreetingModified] = useState(false);

  /**
   * We use an internal state variable to store the greetings so that we can manipulate state without touching the original data.
   * This state is the internal source of truth for greetings only.
   */
  const [greetings, setGreetings] = useState<VoicemailGreeting[]>(data.greetings);

  const { notificationsEnabledWithoutContacts, saveButtonTriggerProps, NotificationTooltip } =
    useNotificationValidation(form);

  const onGreetingsChange = useCallback(
    (incoming: VoicemailGreeting[]) => {
      const isModified =
        data.greetings.length !== incoming.length ||
        data.greetings.some(
          (greeting, index) =>
            greeting.greetingName !== incoming[index].greetingName ||
            greeting.greetingNumber !== incoming[index].greetingNumber
        );
      setGreetings(incoming);
      setIsGreetingModified(isModified);
    },
    [data.greetings]
  );

  useEffect(() => {
    /**
     * This effect is to sync the internal greetings state with updated real data.
     */
    setGreetings(data.greetings);
  }, [data.greetings]);

  useEffect(() => {
    if (onGreetingsChangeFromParent) {
      onGreetingsChangeFromParent(greetings);
    }
  }, [greetings, onGreetingsChangeFromParent]);

  useEffect(() => {
    if (onGreetingModifiedChange) {
      onGreetingModifiedChange(isGreetingModified);
    }
  }, [isGreetingModified, onGreetingModifiedChange]);

  const locationOptions = useMemo(() => {
    return (
      tenantLocation.children?.map((location) => ({
        label: location.name ?? '',
        value: location.locationId ?? '',
      })) ?? []
    );
  }, [tenantLocation]);

  const {
    canEdit: canEditExtension,
    canView: canViewExtension,
    reason: disabledReason,
  } = useMemo(() => {
    return getExtensionConfig({
      mailboxNumber: box.mailbox.number,
      extensionNumber: box.extension?.number,
    });
  }, [box.mailbox.number, box.extension?.number]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      <LocationsAndAccessCard
        locationOptions={locationOptions}
        form={form}
        tenantLocation={tenantLocation}
        canEditExtension={canEditExtension && hasPhoneFullAccess}
        canViewExtension={canViewExtension}
        canEditSharedSetting={hasPhoneFullAccess}
        canEditPin={hasPhoneFullAccess}
        canEditLocations={hasPhoneFullAccess}
        disabledReason={
          hasPhoneFullAccess
            ? disabledReason
            : t('You do not have the necessary permissions to make changes to this setting.')
        }
        tableLayout={tableLayout}
      />
      <MiscellaneousCard form={form} tableLayout={tableLayout} />
      {tableLayout !== 'combined' && (
        <GreetingsCard
          greetings={greetings}
          callObjectUsage={data.callObjectUsage}
          onChange={onGreetingsChange}
          tenantLocation={tenantLocation}
          mailboxId={box.mailbox.id}
        />
      )}
      {!tableLayout || tableLayout === 'separated' ? (
        <>
          <Button
            css={{ width: 'fit-content' }}
            onClick={onSave}
            /**
             * Priority:
             * 1. If the form has an error (or is incomplete) - disable
             * 2. If the form has changed values - enable
             * 3. If notifications are enabled but no email/SMS contacts exist - disable
             */
            disabled={
              !form.isComplete || !(form.changedValues || isGreetingModified) || notificationsEnabledWithoutContacts
            }
            trackingId={trackingId({ context: 'setting', feature: 'voicemail-boxes', details: 'save' })}
            {...(notificationsEnabledWithoutContacts ? saveButtonTriggerProps : {})}
          >
            {t('Save Changes')}
          </Button>
          {NotificationTooltip}
        </>
      ) : null}
    </div>
  );
};

/**
 * The getExtensions query returns an array of all extensions that are in use.
 * We only want to show the extensions that are between 9000 and 9099 (excluding 9001) in the dropdown.
 * We create an array of 99 elements, each element representing an extension number from 9000 to 9099 (excluding 9001)
 * We then map the extensions in use to the corresponding index in the array.
 */
const filterToModifiableExtensions = (extensions: VoicemailBoxExtension[]) => {
  const start = 9000;
  const exception = 9001;

  const arr: [number, VoicemailBoxExtension][] = Array.from({ length: 100 }, (_, i) => {
    return [i + start, { number: `${i + start}` }];
  });

  const map = Object.fromEntries(arr);
  delete map[exception];

  extensions.forEach((ext) => {
    if (ext.number in map) {
      map[ext.number] = ext;
    }
  });

  return Object.values(map);
};

const ExtensionDropdownOption = ({
  value,
  disabled,
  trackingId,
}: {
  value: string;
  disabled?: boolean;
  trackingId?: string;
}) => {
  const { t } = useTranslation('phone');
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'right' });

  return (
    <DropdownField.Option value={value} disabled={disabled} data-trackingid={trackingId}>
      <Text color={disabled ? 'disabled' : 'default'} {...(disabled ? triggerProps : {})}>
        {value}
      </Text>
      <Tooltip {...tooltipProps}>
        <Text color='white' size='medium'>
          {t('Extension already in use.')}
        </Text>
      </Tooltip>
    </DropdownField.Option>
  );
};

const ExtensionSection = ({
  form,
  tenantLocation,
  canEdit,
  canView,
  disabledMessage,
}: {
  form: SchemaForm;
  tenantLocation: SettingsTenantLocation;
  canEdit: boolean;
  canView: boolean;
  disabledMessage?: string;
}) => {
  const { t } = useTranslation('phone');
  const { data: extensions = [] } = useQuery({
    queryKey: [tenantLocation.phoneTenantId, ...queryKeys.settings.listVoicemailExtensions()],

    queryFn: () =>
      VoicemailConfigAPI.ListExtensions({ tenantId: tenantLocation.phoneTenantId ?? '' }).then(
        (res) => res.voicemailboxExtensions
      ),
    select: (data) => filterToModifiableExtensions(data),
    meta: {
      error: {
        message: 'ExtensionSection:extensionsQuery | Failed to fetch voicemail extensions',
        data: { phoneTenantId: tenantLocation.phoneTenantId },
      },
    },
  });
  const extensionFieldProps = form.getFieldProps('extension');

  const { Tooltip: ExtensionTooltip, ...extensionTooltip } = useTooltip({ placement: 'right' });

  if (!canView) {
    return null;
  }

  return (
    <SettingsSection css={{ width: '318px' }}>
      <div {...(!canEdit ? extensionTooltip.triggerProps : {})}>
        <div
          data-tracking-id={trackingId({ context: 'setting', feature: 'voicemail-boxes', details: 'extension-select' })}
          css={{ padding: theme.spacing(0, 0.25) }}
        >
          <DropdownField
            css={{ maxWidth: '530px' }}
            {...extensionFieldProps}
            name='extension'
            label={'Extension (Optional)'}
            helperText={t('Dialing an extension allows the Voicemail Box to be accessed from any Weave phone.')}
            disabled={!canEdit}
          >
            <DropdownField.Option key={'none'} value={'none'}>
              <Text color={!canEdit ? 'disabled' : 'default'}>{t('No Extension')}</Text>
            </DropdownField.Option>
            {extensions.map(({ number: optionNumber, extension: ext, mailboxName }) => {
              const isAvailable = !ext && !mailboxName;

              return <ExtensionDropdownOption key={optionNumber} value={optionNumber} disabled={!isAvailable} />;
            })}
          </DropdownField>
        </div>
        <ExtensionTooltip {...extensionTooltip.tooltipProps}>
          <Text color='white' size='medium'>
            {disabledMessage}
          </Text>
        </ExtensionTooltip>
      </div>
    </SettingsSection>
  );
};

export const LocationsAndAccessCard = ({
  form,
  locationOptions,
  tenantLocation,
  canEditExtension,
  canViewExtension,
  canEditSharedSetting = true,
  canEditPin = true,
  canEditLocations = true,
  disabledReason,
  useTextLabel = false,
  tableLayout,
}: {
  form: SchemaForm;
  locationOptions: { label: string; value: string }[];
  tenantLocation: SettingsTenantLocation;
  canEditExtension: boolean;
  canViewExtension: boolean;
  canEditSharedSetting?: boolean;
  canEditPin?: boolean;
  canEditLocations?: boolean;
  disabledReason?: string;
  useTextLabel?: boolean;
  tableLayout?: 'combined' | 'separated';
}) => {
  const { t } = useTranslation('phone');
  const locationsField = form.getFieldProps('locationIds');
  const shareField = form.getFieldProps('isShared');
  const pinField = form.getFieldProps('pin');
  const { Tooltip: PinTooltip, ...pinTooltip } = useTooltip({ placement: 'right' });
  const { Tooltip: ShareTooltip, ...shareTooltip } = useTooltip({ placement: 'right' });
  const { Tooltip: LocationTooltip, ...locationTooltip } = useTooltip({ placement: 'right' });

  const isUnify = tenantLocation && tenantLocation.locationType === 'unify';

  const content = (
    <>
      {isUnify ? (
        <SettingsSection
          css={css`
            width: 100%;
          `}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(3),
              width: '100%',
              maxWidth: '608px',
            }}
            {...(!canEditLocations ? { ...locationTooltip.triggerProps } : {})}
          >
            <div>
              {tableLayout !== 'combined' && (
                <Heading level={2} css={{ marginBottom: theme.spacing(3) }}>
                  {t('Locations and Access')}
                </Heading>
              )}
              {useTextLabel ? (
                <Text size='large' weight='bold'>
                  {t('Locations (Optional)')}
                </Text>
              ) : (
                <Heading level={3}>{t('Locations (Optional)')}</Heading>
              )}
              <Text size='medium'>{t('Select the locations that will be associated with this Voicemail Box.')}</Text>
            </div>
            <div
              data-tracking-id={trackingId({
                context: 'setting',
                feature: 'voicemail-boxes',
                details: 'location-select',
              })}
              css={{ padding: theme.spacing(0, 0.25) }}
            >
              <ChecklistMenuField
                {...locationsField}
                disabled={!canEditLocations}
                label={t('Locations')}
                name='locationIds'
                helperText={t('Selecting none will auto-assign all locations to this Voicemail Box.')}
              >
                {locationOptions.map((option) => (
                  <ChecklistMenuField.Option key={option.value} value={option.value}>
                    {option.label}
                  </ChecklistMenuField.Option>
                ))}
              </ChecklistMenuField>
            </div>
          </div>
          <ShareTooltip {...locationTooltip.tooltipProps}>
            <Text color='white' size='medium'>
              {t('You do not have the necessary permissions to make changes to this setting.')}
            </Text>
          </ShareTooltip>
        </SettingsSection>
      ) : null}
      <SettingsSectionCombinedLayout>
        <div
          {...(!canEditSharedSetting ? { ...shareTooltip.triggerProps } : {})}
          css={{ width: '100%', padding: theme.spacing(0, 0.5) }}
        >
          <Heading level={3} css={{ marginBottom: theme.spacing(2) }}>
            {t('Share in App')}
          </Heading>
          <CheckboxField
            {...shareField}
            disabled={!canEditSharedSetting}
            name='isShared'
            css={[
              css`
                width: fit-content;
                label {
                  opacity: 1;
                }
              `,
              checkboxRow,
            ]}
            label={
              useTextLabel ? (
                <Text size='large' weight='bold'>
                  {t('Share in App')}
                </Text>
              ) : (
                <Text> {t('Share in App')}</Text>
              )
            }
            labelPlacement='right'
            trackingId={trackingId({ context: 'setting', feature: 'voicemail-boxes', details: 'shared-access' })}
          />
          <Text size='medium' css={checkboxAlignedText}>
            {isUnify
              ? t('Users in associated locations can access this Voicemail Box in Weave apps.')
              : t('Users can access this Voicemail Box in Weave apps.')}
          </Text>
        </div>
        <ShareTooltip {...shareTooltip.tooltipProps}>
          <Text color='white' size='medium'>
            {t('You do not have the necessary permissions to make changes to this setting.')}
          </Text>
        </ShareTooltip>
      </SettingsSectionCombinedLayout>
      <ExtensionSection
        form={form}
        tenantLocation={tenantLocation}
        canEdit={canEditExtension}
        canView={canViewExtension}
        disabledMessage={disabledReason}
      />
      <SettingsSection css={{ width: '318px' }}>
        <div
          {...(!canEditPin ? { ...pinTooltip.triggerProps } : {})}
          css={{ width: '100%', padding: theme.spacing(0, 0.25) }}
        >
          <TextField
            css={{ width: theme.spacing(40) }}
            {...pinField}
            disabled={!canEditPin}
            name='pin'
            label={t('PIN (Optional)')}
            helperText={t('Adding a PIN will require users to enter the PIN when accessing the Voicemail Box.')}
          />
        </div>
        <PinTooltip {...pinTooltip.tooltipProps}>
          <Text color='white' size='medium'>
            {t('You do not have the necessary permissions to make changes to this setting.')}
          </Text>
        </PinTooltip>
      </SettingsSection>
    </>
  );

  return tableLayout === 'combined' ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>{content}</div>
  ) : (
    <SettingsCard css={{ width: '318px', padding: theme.spacing(0, 0.25) }}>{content}</SettingsCard>
  );
};

const variants = {
  open: {
    height: 'auto',
    opacity: 1,
    transition: {
      ease: 'easeInOut',
      opacity: { duration: 0.3 },
      height: { duration: 0.1 },
    },
  },
  closed: {
    height: 0,
    opacity: 0,
    transition: {
      ease: 'easeInOut',
      opacity: { duration: 0.15 },
      height: { duration: 0.3 },
    },
  },
};

export const MiscellaneousCard = ({
  form,
  useTextLabel,
  tableLayout,
}: {
  form: SchemaForm;
  useTextLabel?: boolean;
  tableLayout?: 'combined' | 'separated';
}) => {
  const { t } = useTranslation('phone');
  const playDateField = form.getFieldProps('playMessageDateEnabled');
  const notificationEnabledField = form.getFieldProps('notificationsEnabled');
  const emailField = form.getFieldProps('notificationEmail');
  const smsField = form.getFieldProps('notificationSMS');

  const content = (
    <>
      <SettingsSectionCombinedLayout>
        <div css={{ width: '100%', padding: theme.spacing(0, 0.5) }}>
          <Heading level={3} css={{ marginBottom: theme.spacing(2) }}>
            {t('Play Message')}
          </Heading>
          <CheckboxField
            {...playDateField}
            css={[
              css`
                width: fit-content;
                label {
                  opacity: 1;
                }
              `,
              checkboxRow,
            ]}
            name='playMessageDateEnabled'
            label={
              useTextLabel ? (
                <Text size='large' weight='bold'>
                  {t('Play Message Date')}
                </Text>
              ) : (
                <Text> {t('Play Message Date')}</Text>
              )
            }
            labelPlacement='right'
          />
          <Text size='medium' css={checkboxAlignedText}>
            {t('Play the date that the voicemail message was delivered for each recording.')}
          </Text>
        </div>
      </SettingsSectionCombinedLayout>

      <SettingsSectionCombinedLayout>
        <div css={{ width: '100%', padding: theme.spacing(0, 0.5) }}>
          <Heading level={3} css={{ marginBottom: theme.spacing(2) }}>
            {t('Email & SMS Notifications')}
          </Heading>
          <CheckboxField
            {...notificationEnabledField}
            css={[
              css`
                width: fit-content;
                label {
                  opacity: 1;
                }
              `,
              checkboxRow,
            ]}
            name='notificationsEnabled'
            label={
              <Text>
                {useTextLabel ? <Text>{t('Enable Notifications')}</Text> : <Text> {t('Enable Notifications')}</Text>}
              </Text>
            }
            labelPlacement='right'
          />
          <Text size='medium' css={checkboxAlignedText}>
            {t('Receive a notification when you have a new voicemail.')}
          </Text>
          <AnimatePresence>
            {notificationEnabledField.value && (
              <motion.div
                variants={variants}
                animate='open'
                initial='closed'
                exit='closed'
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing(3),
                  padding: theme.spacing(2, 0.25),
                }}
              >
                <>
                  <ListField
                    label={t('Add Email')}
                    fieldType='email'
                    disabled={!notificationEnabledField.value}
                    {...emailField}
                    css={{ width: theme.spacing(40) }}
                  />
                  <ListField
                    label={t('Add SMS')}
                    fieldType='phone'
                    disabled={!notificationEnabledField.value}
                    {...smsField}
                    css={{ width: theme.spacing(40) }}
                  />
                </>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </SettingsSectionCombinedLayout>
    </>
  );

  return tableLayout === 'combined' ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>{content}</div>
  ) : (
    <SettingsCard>{content}</SettingsCard>
  );
};

const checkboxRow = css`
  grid-area: checkbox-row;
  align-items: center;
`;

const checkboxAlignedText = css`
  margin-left: ${theme.spacing(3.5)};
`;

const SettingsSectionCombinedLayout = ({ children, className }: { children: ReactNode; className?: string }) => {
  return (
    <section
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
      }}
      className={className}
    >
      {children}
    </section>
  );
};
